import { useNavigate } from "react-router-dom";
import { Chip, StatusChip } from "@/components/StatusChip";
import {
  ShieldAlert, ReceiptText, TrendingUp, FileWarning, ListChecks, CircleCheck,
  Sparkles, ArrowUpRight, Layers,
} from "lucide-react";
import { fmtUSD, initials } from "@/lib/api";

const TONE_COLOR = { green: "#4ADE80", amber: "#FBBF24", red: "#F87171", blue: "#7FAEF9", violet: "#A78BFA", neutral: "#ffffff" };
const SEVERITY_TONE = { high: "red", medium: "amber", low: "neutral" };

const MetricCard = ({ c }) => (
  <div className="rounded-xl border border-[#222] bg-[#0d0d0d] p-3.5" data-testid="aria-card-metric">
    <div className="text-[10px] uppercase tracking-wider text-[#6b6b6b]">{c.label}</div>
    <div className="mt-1 text-[22px] font-semibold leading-none tabular-nums" style={{ color: TONE_COLOR[c.tone] || "#fff" }}>{c.value}</div>
    <div className="mt-1 text-[10.5px] text-[#6b6b6b]">{c.sub}</div>
  </div>
);

const EmployeeCard = ({ c }) => {
  const navigate = useNavigate();
  return (
    <button onClick={() => c.employee_id && navigate(`/employees/${c.employee_id}`)}
      data-testid="aria-card-employee"
      className="group card-lift rounded-xl border border-[#222] bg-[#0d0d0d] p-3.5 text-left">
      <div className="flex items-center gap-2.5">
        <span className="flex h-9 w-9 items-center justify-center rounded-full text-[11px] font-bold"
          style={{ background: `${c.avatar_color}22`, color: c.avatar_color }}>{initials(c.name)}</span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-medium text-white">{c.name}</div>
          <div className="truncate text-[10.5px] text-[#6b6b6b]">{c.role} · {c.department}</div>
        </div>
        <ArrowUpRight size={13} className="text-[#555] group-hover:text-[#7FAEF9]" />
      </div>
      {c.card_kind === "violation" && (
        <div className="mt-2.5">
          <div className="flex items-center gap-1.5 text-[12px]">
            <span className="flex items-center gap-1 font-medium text-[#F87171]"><ShieldAlert size={12} /> {c.violation_count} violations</span>
            <span className="text-[#6b6b6b]">· {fmtUSD(c.total_amount)}</span>
          </div>
          {c.top_violation && <div className="mt-1 line-clamp-2 text-[10.5px] text-[#A3A3A3]">{c.top_violation}</div>}
        </div>
      )}
      {c.card_kind === "pending" && (
        <div className="mt-2.5 flex items-center gap-1.5 text-[12px]">
          <span className="flex items-center gap-1 font-medium text-[#FBBF24]"><ReceiptText size={12} /> {c.pending_count} missing</span>
          <span className="text-[#6b6b6b]">· {fmtUSD(c.total_amount)}</span>
        </div>
      )}
      {c.card_kind === "spend" && (
        <div className="mt-2.5 flex items-center gap-1.5 text-[12px]">
          <span className="font-semibold tabular-nums text-white">{fmtUSD(c.total_amount)}</span>
          <span className="text-[#6b6b6b]">· {c.expense_count} expenses</span>
        </div>
      )}
    </button>
  );
};

const PolicyCard = ({ c }) => (
  <div className="rounded-xl border border-[#222] bg-[#0d0d0d] p-3.5" data-testid="aria-card-policy">
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="truncate text-[13px] font-medium text-white">{c.name}</div>
        <div className="text-[10.5px] text-[#6b6b6b]">{c.category}</div>
      </div>
      <Chip tone={SEVERITY_TONE[c.severity] || "neutral"}>{c.severity}</Chip>
    </div>
    <div className="mt-2.5 flex items-baseline gap-1.5">
      <span className="text-[20px] font-semibold tabular-nums" style={{ color: c.violation_count ? "#F87171" : "#4ADE80" }}>{c.violation_count}</span>
      <span className="text-[11px] text-[#6b6b6b]">violations this period</span>
    </div>
    {c.sample && <div className="mt-1 line-clamp-2 text-[10.5px] text-[#A3A3A3]">{c.sample}</div>}
  </div>
);

const ExpenseCard = ({ c }) => (
  <div className="rounded-xl border border-[#222] bg-[#0d0d0d] p-3.5" data-testid="aria-card-expense">
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="truncate text-[13px] font-medium text-white">{c.merchant}</div>
        <div className="truncate text-[10.5px] text-[#6b6b6b]">{c.employee_name} · {c.category}</div>
      </div>
      <span className="shrink-0 text-[13px] font-semibold tabular-nums text-white">{fmtUSD(c.amount)}</span>
    </div>
    <div className="mt-2 flex items-center gap-1.5">
      {c.status && <StatusChip status={c.status} />}
      {c.policy_status === "violation" && <Chip tone="red">Violation</Chip>}
    </div>
    {c.violation_reason && <div className="mt-1 line-clamp-2 text-[10.5px] text-[#F87171]">{c.violation_reason}</div>}
  </div>
);

const CategoryCard = ({ c, max }) => (
  <div className="rounded-xl border border-[#222] bg-[#0d0d0d] p-3.5" data-testid="aria-card-category">
    <div className="flex items-center justify-between text-[12px]">
      <span className="font-medium text-white">{c.category}</span>
      <span className="tabular-nums text-[#A3A3A3]">{fmtUSD(c.amount)}</span>
    </div>
    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[#1e1e1e]">
      <div className="h-full rounded-full bg-[#4F8EF7]" style={{ width: `${Math.max(6, (c.amount / (max || 1)) * 100)}%` }} />
    </div>
    <div className="mt-1 text-[10px] text-[#6b6b6b]">{c.count} expenses</div>
  </div>
);

const KIND_ICON = { nudge: ReceiptText, action: ListChecks, manual: Layers };

const TaskCard = ({ c }) => {
  const Icon = KIND_ICON[c.kind] || ListChecks;
  const done = c.status === "done";
  return (
    <div className="rounded-xl border border-[rgba(79,142,247,0.25)] bg-[rgba(79,142,247,0.05)] p-3.5 sm:col-span-2" data-testid="aria-card-task">
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-[rgba(79,142,247,0.3)] bg-[rgba(79,142,247,0.12)] text-[#7FAEF9]">
          <Icon size={13} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-medium text-white">{c.title}</span>
            <Chip tone={done ? "green" : "blue"}>{done ? "Done" : "Tracking"}</Chip>
          </div>
          <div className="mt-0.5 text-[11px] text-[#A3A3A3]">{c.detail}</div>
        </div>
      </div>
    </div>
  );
};

const ACTION_ICON = { green: CircleCheck, amber: ReceiptText, red: FileWarning };

const StrategyCard = ({ c }) => {
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate(`/communications?tab=strategies${c.strategy_id ? `&edit=${c.strategy_id}` : ""}`)}
      data-testid="aria-card-strategy"
      className="group card-lift rounded-xl border border-[#222] bg-[#0d0d0d] p-3.5 text-left sm:col-span-2">
      <div className="flex items-center gap-2">
        <span className="truncate text-[13px] font-medium text-white">{c.name}</span>
        {c.is_default && <Chip tone="blue">Default</Chip>}
        <ArrowUpRight size={13} className="ml-auto shrink-0 text-[#555] group-hover:text-[#7FAEF9]" />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1 text-[10.5px] text-[#A3A3A3]">
        {(c.steps || []).map((s, i) => (
          <span key={i} className="flex items-center gap-1">
            {i > 0 && <span className="text-[#555]">→ {s.wait_hours}h →</span>}
            <span className="rounded-md bg-[#1a1a1a] px-1.5 py-0.5 text-[#e5e5e5]">{s.channel} · {s.tone}</span>
          </span>
        ))}
      </div>
    </button>
  );
};

const ActionResultCard = ({ c }) => {
  const color = TONE_COLOR[c.tone] || "#7FAEF9";
  const Icon = ACTION_ICON[c.tone] || Sparkles;
  return (
    <div className="flex items-center gap-3 rounded-xl border p-3.5 sm:col-span-2" data-testid="aria-card-action"
      style={{ borderColor: `${color}44`, background: `${color}0f` }}>
      <span className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: `${color}1c`, color }}>
        <Icon size={18} />
      </span>
      <div>
        <div className="flex items-baseline gap-2">
          <span className="text-[15px] font-semibold text-white">{c.label}</span>
          <span className="text-[13px] font-semibold tabular-nums" style={{ color }}>{c.count}</span>
        </div>
        <div className="text-[11px] text-[#A3A3A3]">{c.detail}</div>
      </div>
    </div>
  );
};

export const AriaCardGrid = ({ cards }) => {
  if (!cards || cards.length === 0) return null;
  const catMax = Math.max(1, ...cards.filter((c) => c.type === "category").map((c) => c.amount));
  return (
    <div className="mt-2.5 grid gap-2.5 sm:grid-cols-2" data-testid="aria-cards">
      {cards.map((c, i) => {
        switch (c.type) {
          case "metric": return <MetricCard key={i} c={c} />;
          case "employee": return <EmployeeCard key={i} c={c} />;
          case "policy": return <PolicyCard key={i} c={c} />;
          case "expense": return <ExpenseCard key={i} c={c} />;
          case "category": return <CategoryCard key={i} c={c} max={catMax} />;
          case "task": return <TaskCard key={i} c={c} />;
          case "strategy": return <StrategyCard key={i} c={c} />;
          case "action_result": return <ActionResultCard key={i} c={c} />;
          default: return null;
        }
      })}
    </div>
  );
};
